import React, { useState, useEffect, useRef } from 'react'
import {
    Modal,
    Box,
    Typography,
    IconButton,
    Button,
    TextField,
    CircularProgress,
    Alert,
    Stack,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { useAuthInfo } from '@propelauth/react'
import { 
    listAllMRTTablesFromDocumentPathAndConnectorId,
    uploadMultipleFilesUpdated
} from '../../../utils/utils'
import { useSourceDocumentStatus } from '../../../hooks/useSourceDocumentStatus'
import { useTableAssignments } from '../../../hooks/useTableAssignments'
import { useModalPersistence } from '../../../hooks/useModalPersistence'
import { tableAssignmentStorage } from '../../../services/TableAssignmentStorage'
import CustomTable from '../../../components/CustomTable'
import { CustomDropdownMenu } from '../../../components/CustomSelect'
import { ContinueButton } from '../../../components/Buttons'
import { SecondaryButtonFactory } from '../../../components/Buttons/SecondaryButton'
import { v4 as uuidv4 } from 'uuid'
import GeneratingTemplateModal from '../../TemplatesPages/steps/modals/GeneratingTemplateModal'

const AssignTablesModal = ({
    open,
    handleClose,
    documentMRTTables = [],
    onInsertAll,
    sourceDocument = null,
    connectorId = null
}) => {
    const authInfo = useAuthInfo()
    const { getDocumentFilePath, setDocumentFilePath } = useSourceDocumentStatus()
    
    // Use the custom hook for table assignments management
    const {
        tableAssignments,
        isLoading: assignmentsLoading,
        hasUnsavedChanges,
        documentId,
        updateAssignment,
        updateReplacementTable,
        addAssignment,
        removeAssignment,
        removeAllAssignments,
        saveAssignments,
        clearStoredAssignments,
        getPreparedAssignments,
        getStatistics
    } = useTableAssignments(documentMRTTables, sourceDocument)
    
    const [editingInstruction, setEditingInstruction] = useState(null)
    const [replacementTableOptions, setReplacementTableOptions] = useState([])
    const [templateTableOptions, setTemplateTableOptions] = useState([])
    const lastRefreshTimeRef = useRef(null)
    const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false)
    const [templateDropdownAnchorEl, setTemplateDropdownAnchorEl] = useState(null)
    const [templateDropdownRowId, setTemplateDropdownRowId] = useState(null)
    const [replacementDropdownOpen, setReplacementDropdownOpen] = useState(false)
    const [replacementDropdownAnchorEl, setReplacementDropdownAnchorEl] = useState(null)
    const [replacementDropdownRowId, setReplacementDropdownRowId] = useState(null)
    const [highlightedRowId, setHighlightedRowId] = useState(null)
    const textFieldRef = useRef(null)
    const scrollContainerRef = useRef(null)
    const [isProcessing, setIsProcessing] = useState(false)
    const [showRegenerationModal, setShowRegenerationModal] = useState(false)
    const [regenerationStatus, setRegenerationStatus] = useState({ loading: false, success: false, error: false })
    const [pendingMappings, setPendingMappings] = useState(null)
    const [needsDocumentUpload, setNeedsDocumentUpload] = useState(false)
    const [uploadModalOpen, setUploadModalOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const fileInputRef = useRef(null)
    
    // Use modal persistence hook
    const { createCloseHandler } = useModalPersistence(saveAssignments, open)
    
    // Enhanced close handler that saves before closing
    const handleCloseWithSave = createCloseHandler(handleClose)
    
    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files)
        if (files.length === 0) return
        
        const fileContents = await Promise.all(
            files.map(
                (file) =>
                    new Promise((resolve) => {
                        const reader = new FileReader()
                        reader.onload = (e) => resolve(e.target.result)
                        reader.readAsArrayBuffer(file)
                    })
            )
        )

        const fileName = files[0].name
        const filePath = "input/" + fileName
        console.log('Uploading file:', filePath)
        
        await handleFileUpload(files, fileContents, filePath)
    }
    
    const handleFileUpload = async (files, fileContents, filePath) => {
        try {
            setLoading(true)
            const orgs = authInfo.orgHelper.getOrgs()
            const orgId = orgs[0].orgId
            const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(orgId)
            const currentFolder = "input"

            await uploadMultipleFilesUpdated(
                setLoading,
                files.map((file, index) => ({
                    name: file.name,
                    content: fileContents[index],
                })),
                fileContents,
                (error) => {
                    console.error('Upload error:', error)
                },
                currentFolder,
                orgAccessToken
            )
            
            // Store the document-file relationship
            if (sourceDocument?.document_id) {
                setDocumentFilePath(sourceDocument.document_id, filePath, connectorId)
            }
            
            // Reset the upload state and refresh tables
            setNeedsDocumentUpload(false)
            setUploadModalOpen(false)
            
            // Refresh the replacement table options with the new document
            await fetchReplacementTableOptions()
            
        } catch (error) {
            console.error('Upload failed:', error)
        } finally {
            setLoading(false)
        }
    }
    
    // Clean up dropdowns when modal closes or unmounts
    useEffect(() => {
        if (!open) {
            // Reset all dropdown states when modal is closed
            setTemplateDropdownOpen(false)
            setTemplateDropdownAnchorEl(null)
            setTemplateDropdownRowId(null)
            setReplacementDropdownOpen(false)
            setReplacementDropdownAnchorEl(null)
            setReplacementDropdownRowId(null)
        } else {
            // Reset upload state when modal opens
            setNeedsDocumentUpload(false)
        }
    }, [open])
    
    // Clean up on unmount and visibility change
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                // Close dropdowns when tab goes to background
                setTemplateDropdownOpen(false)
                setTemplateDropdownAnchorEl(null)
                setTemplateDropdownRowId(null)
                setReplacementDropdownOpen(false)
                setReplacementDropdownAnchorEl(null)
                setReplacementDropdownRowId(null)
            }
        }
        
        // Add event listener
        document.addEventListener('visibilitychange', handleVisibilityChange)
        
        // Cleanup function
        return () => {
            // Remove event listener
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            
            // Clean up dropdown states on unmount
            setTemplateDropdownOpen(false)
            setTemplateDropdownAnchorEl(null)
            setTemplateDropdownRowId(null)
            setReplacementDropdownOpen(false)
            setReplacementDropdownAnchorEl(null)
            setReplacementDropdownRowId(null)
        }
    }, [])

    // Load template tables from localStorage or props on mount
    useEffect(() => {
        if (!sourceDocument?.document_id) {
            // No document ID, use documentMRTTables directly
            setTemplateTableOptions(documentMRTTables || [])
            return
        }

        // Try to load from localStorage first
        const storedTemplateTables = tableAssignmentStorage.loadTemplateTables(sourceDocument.document_id)
        
        if (storedTemplateTables && storedTemplateTables.length > 0) {
            console.log('Using stored template tables')
            setTemplateTableOptions(storedTemplateTables)
        } else if (documentMRTTables && documentMRTTables.length > 0) {
            console.log('Saving template tables to localStorage')
            setTemplateTableOptions(documentMRTTables)
            tableAssignmentStorage.saveTemplateTables(sourceDocument.document_id, documentMRTTables)
        }
    }, [documentMRTTables, sourceDocument])

    useEffect(() => {
        if (sourceDocument?.document_id && connectorId) {
            // Check if we have stored replacement tables
            const storedData = tableAssignmentStorage.loadReplacementTables(sourceDocument.document_id)
            
            // Always fetch from API if storage is empty or null
            if (!storedData || !storedData.tables || storedData.tables.length === 0) {
                console.log('No stored replacement tables, fetching from API')
                fetchReplacementTableOptions()
                return
            }
            
            // Check if data is older than 5 minutes
            const savedTime = new Date(storedData.savedAt)
            const now = new Date()
            const diffMinutes = (now - savedTime) / (1000 * 60)
            
            if (diffMinutes < 5) {
                console.log('Using cached replacement tables')
                setReplacementTableOptions(storedData.tables)
                lastRefreshTimeRef.current = savedTime
                
                // Set up refresh after remaining time
                const remainingMs = (5 * 60 * 1000) - (now - savedTime)
                setTimeout(() => fetchReplacementTableOptions(true), remainingMs)
            } else {
                // Data is older than 5 minutes, fetch fresh data
                console.log('Cached data is stale, fetching fresh replacement tables')
                fetchReplacementTableOptions(true)
            }
        }
    }, [sourceDocument, connectorId])

    // Set up 5-minute refresh interval for replacement tables
    useEffect(() => {
        if (!sourceDocument?.document_id || !connectorId) return
        
        const interval = setInterval(() => {
            console.log('Auto-refreshing replacement tables (5-minute interval)')
            fetchReplacementTableOptions(true)
        }, 5 * 60 * 1000) // 5 minutes
        
        return () => clearInterval(interval)
    }, [sourceDocument, connectorId])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (textFieldRef.current && !textFieldRef.current.contains(event.target)) {
                setEditingInstruction(null)
            }
        }

        if (editingInstruction !== null) {
            document.addEventListener('mousedown', handleClickOutside)
            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
            }
        }
    }, [editingInstruction])

    const fetchReplacementTableOptions = async (isRefresh = false) => {
        try {
            setLoading(true)
            const orgs = authInfo.orgHelper.getOrgs()
            const orgId = orgs[0].orgId
            const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(orgId)
            
            let allOptions = []
            // If we have a specific source document, fetch tables for that document
            if (sourceDocument) {
                // Get the document file path using the document ID
                const documentPathObject = getDocumentFilePath(sourceDocument.document_id)

                console.log('documentPathObject', documentPathObject)
                console.log('sourceDocument.document_source_path', sourceDocument.document_source_path)

                // Try to get file path from the relationship first
                let documentPath = documentPathObject?.filePath
                let connectorDataId = documentPathObject?.connectorId
                
                // If documentPath is not found, the user needs to upload their document
                if (!documentPath) {
                    console.log('No user document found - need to upload')
                    setNeedsDocumentUpload(true)
                    setLoading(false)
                    return
                }
                
                // Normalize the document path if it exists
                if (documentPath) {
                    // Remove leading slash if present for consistency
                    if (documentPath.startsWith('/')) {
                        documentPath = documentPath.slice(1)
                    }
                }

                console.log('Final documentPath', documentPath)
                
                if (documentPath) {
                    if (documentPath.startsWith(`${orgId}/`)) {
                        documentPath = documentPath.split(`/`).slice(1).join(`/`)
                        console.log('documentPath', documentPath)
                    } else {
                        console.log('documentPath does not start with orgId', documentPath)
                    }

                    const payload = {
                        file_path: documentPath,
                        connector_data_id: connectorDataId || connectorId
                    }

                    console.log('payload', payload)

                    const response = await listAllMRTTablesFromDocumentPathAndConnectorId(payload, orgAccessToken)

                    console.log('response', response)
                    console.log('response.tables', response?.tables)

                    const tables = response?.tables || response || []
                    
                    // Append-only behavior: Load existing stored tables first
                    const storedData = sourceDocument?.document_id 
                        ? tableAssignmentStorage.loadReplacementTables(sourceDocument.document_id)
                        : null
                    
                    const existingTables = storedData?.tables || []
                    
                    if (isRefresh && existingTables.length > 0) {
                        // Create a map of existing tables by ID for quick lookup
                        const existingTablesMap = new Map(
                            existingTables.map(t => [t.id, t])
                        )
                        
                        // Process API tables
                        const processedApiTables = tables.map(table => {
                            const existing = existingTablesMap.get(table.id)
                            console.log('existing', existing)
                            if (existing) {
                                // Table exists - preserve UUID
                                return {
                                    ...table,
                                    uuid: existing.uuid
                                }
                            } else {
                                // New table from API
                                return {
                                    ...table,
                                    uuid: uuidv4()
                                }
                            }
                        })
                        
                        // Append-only: Start with ALL existing tables
                        allOptions = [...existingTables]
                        
                        // Add new tables from API that don't exist in storage
                        processedApiTables.forEach(apiTable => {
                            const existingIndex = allOptions.findIndex(t => t.id === apiTable.id)
                            if (existingIndex === -1) {
                                // New table not in storage - append it
                                allOptions.push(apiTable)
                            }
                        })
                        
                        console.log(`Append-only merge: Started with ${existingTables.length} stored, API had ${tables.length}, final ${allOptions.length}`)
                    } else {
                        // First load or no existing data - assign new UUIDs to all
                        allOptions = tables.map(table => ({
                            ...table,
                            uuid: uuidv4()
                        }))
                        console.log(`Initial load: ${allOptions.length} tables from API`)
                    }
                    
                    console.log('allOptions', allOptions)
                    console.log('templateTableOptions', templateTableOptions)
                } else {
                    console.warn('Could not find document path for document ID:', sourceDocument.document_id, {
                        sourceDocument,
                        documentPathObject,
                        availableKeys: Object.keys(sourceDocument || {})
                    })
                    // Even without a document path, we might still have some tables from other sources
                    allOptions = []
                }
            }
            
            setReplacementTableOptions(allOptions)
            
            // Save to localStorage with append-only behavior
            if (sourceDocument?.document_id) {
                tableAssignmentStorage.saveReplacementTables(sourceDocument.document_id, allOptions, true)
                lastRefreshTimeRef.current = new Date()
            }
        } catch (error) {
            console.error('Failed to fetch replacement table options:', error)
            setReplacementTableOptions([])
        } finally {
            setLoading(false)
        }
    }




    const handleRemoveRow = (rowId) => {
        removeAssignment(rowId)
    }

    const columns = [
        {
            header: "",
            field: 'delete',
            width: '5%',
            render: (_, row) => {
                return (
                    <IconButton onClick={() => handleRemoveRow(row.id)}>
                        <DeleteIcon />
                    </IconButton>
                )
            }
        },
        {
            header: 'Section',
            field: 'section',
            width: '5%',
        },
        {
            header: 'Template table',
            field: 'templateTable',
            width: '27.5%',
            render: (_, row) => {
                const templateOptions = templateTableOptions.map(table => {
                    return {
                        value: table.uuid,
                        label: table?.extracted_table_title ? table.extracted_table_title : table.table_title
                    }
                })

                if (row.templateTable === '') {
                    return (
                        <Box sx={{ position: 'relative' }}>
                            <Button
                                size="small"
                                onClick={(event) => handleTemplateDropdownClick(event, row.id)}
                                sx={{ 
                                    textTransform: 'none',
                                    p: '0px',
                                    borderRadius: '6px',
                                    boxShadow: 'inset 0px 0px 0px 1px #D1D5DB',
                                    backgroundColor: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#F9FAFB',
                                    }
                                }}
                            >
                                <Typography sx={{ 
                                    lineHeight: '16px', 
                                    fontSize: '14px', 
                                    fontWeight: 'medium', 
                                    color: '#374151', 
                                    py: '9px', 
                                    px: '15px' 
                                }}>
                                    Select data table
                                </Typography>
                            </Button>
                            <CustomDropdownMenu
                                isOpen={templateDropdownOpen && templateDropdownRowId === row.id}
                                anchorEl={templateDropdownAnchorEl}
                                onClose={handleTemplateDropdownClose}
                                onSelect={handleTemplateDropdownSelect}
                                options={templateOptions}
                                selectedOptions={row.templateTable}
                                proportionalPosition={getTemplateDropdownPosition()}
                                maxHeight="200px"
                                dataAttributePrefix="template"
                                dropdownId={`template-dropdown-${row.id}`}
                            />
                        </Box>
                    )
                }
                return (
                    <Box sx={{ position: 'relative' }}>
                        <Typography 
                            variant="body2" 
                            onClick={(event) => handleTemplateDropdownClick(event, row.id)}
                            data-template-text
                            sx={{ 
                                fontSize: '14px', 
                                color: '#374151',
                                cursor: 'pointer',
                                padding: '4px',
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: '#F9FAFB',
                                }
                            }}
                        >
                            {getTemplateTableLabel(row.templateTable)}
                        </Typography>
                        <CustomDropdownMenu
                            isOpen={templateDropdownOpen && templateDropdownRowId === row.id}
                            anchorEl={templateDropdownAnchorEl}
                            onClose={handleTemplateDropdownClose}
                            onSelect={handleTemplateDropdownSelect}
                            options={templateOptions}
                            selectedOptions={row.templateTable}
                            proportionalPosition={getTemplateDropdownPosition()}
                            maxHeight="200px"
                            dataAttributePrefix="template"
                            dropdownId={`template-dropdown-${row.id}`}
                        />
                    </Box>
                )
            }
        },
        {
            header: '',
            field: 'edit',
            width: '5%',
            render: (_, row) => {
                if (row.templateTable === '') {
                    return
                } else {
                    return (
                        <IconButton onClick={(event) => {
                            handleTemplateDropdownClick(event, row.id, true)
                        }}>
                            <EditIcon sx={{ height: '16px', width: '16px' }} />
                        </IconButton>
                    )
                }
            }
        },
        {
            header: 'Replacement table',
            field: 'replacementTable',
            width: '27.5%',
            render: (_, row) => {
                const replacementOptions = replacementTableOptions.map(table => {
                    return {
                        value: table.uuid,
                        label: table?.extracted_table_title ? table.extracted_table_title : table.table_title
                    }
                })
                
                if (row.replacementTable === '') {
                    return (
                        <Box sx={{ position: 'relative' }}>
                            <Button
                                size="small"
                                onClick={(event) => handleReplacementDropdownClick(event, row.id)}
                                sx={{ 
                                    textTransform: 'none',
                                    p: '0px',
                                    borderRadius: '6px',
                                    boxShadow: 'inset 0px 0px 0px 1px #D1D5DB',
                                    backgroundColor: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#F9FAFB',
                                    }
                                }}
                            >
                                <Typography sx={{ 
                                    lineHeight: '16px', 
                                    fontSize: '14px', 
                                    fontWeight: 'medium', 
                                    color: '#374151', 
                                    py: '9px', 
                                    px: '15px' 
                                }}>
                                    Select data table
                                </Typography>
                            </Button>
                            <CustomDropdownMenu
                                isOpen={replacementDropdownOpen && replacementDropdownRowId === row.id}
                                anchorEl={replacementDropdownAnchorEl}
                                onClose={handleReplacementDropdownClose}
                                onSelect={handleReplacementDropdownSelect}
                                options={replacementOptions}
                                selectedOptions={row.replacementTable}
                                proportionalPosition={getReplacementDropdownPosition()}
                                maxHeight="200px"
                                dataAttributePrefix="replacement"
                                dropdownId={`replacement-dropdown-${row.id}`}
                            />
                        </Box>
                    )
                }
                return (
                    <Box sx={{ position: 'relative' }}>
                        <Typography 
                            variant="body2" 
                            onClick={(event) => handleReplacementDropdownClick(event, row.id)}
                            data-replacement-text
                            sx={{ 
                                fontSize: '14px', 
                                color: '#374151',
                                cursor: 'pointer',
                                padding: '4px',
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: '#F9FAFB',
                                }
                            }}
                        >
                            {getReplacementTableLabel(row.replacementTable)}
                        </Typography>
                        <CustomDropdownMenu
                            isOpen={replacementDropdownOpen && replacementDropdownRowId === row.id}
                            anchorEl={replacementDropdownAnchorEl}
                            onClose={handleReplacementDropdownClose}
                            onSelect={handleReplacementDropdownSelect}
                            options={replacementOptions}
                            selectedOptions={row.replacementTable}
                            proportionalPosition={getReplacementDropdownPosition()}
                            maxHeight="200px"
                            dataAttributePrefix="replacement"
                            dropdownId={`replacement-dropdown-${row.id}`}
                        />
                    </Box>
                )
            }
        },
        {
            header: '',
            field: 'edit',
            width: '5%',
            render: (_, row) => {
                if (row.replacementTable === '') {
                    return
                } else {
                    return (
                        <IconButton onClick={(event) => {
                            handleReplacementDropdownClick(event, row.id, true)
                        }}>
                            <EditIcon sx={{ height: '16px', width: '16px' }} />
                        </IconButton>
                    )
                }
            }
        },
        {
            header: 'Instructions (optional)',
            field: 'instructions',
            width: '20%',
            render: (_, row) => {
                if (row.instructions === '' && editingInstruction !== row.id) {
                    return (
                        <Button
                            size="small"
                            onClick={() => handleEditingInstruction(row)}
                            sx={{ 
                                textTransform: 'none',
                                p: '0px',
                                borderRadius: '6px',
                                boxShadow: 'inset 0px 0px 0px 1px #D1D5DB',
                                backgroundColor: '#FFFFFF',
                                '&:hover': {
                                    backgroundColor: '#F9FAFB',
                                }
                            }}
                        >
                            <Typography sx={{ 
                                lineHeight: '16px', 
                                fontSize: '14px', 
                                fontWeight: 'medium', 
                                color: '#374151', 
                                py: '9px', 
                                px: '15px' 
                            }}>
                                Add
                            </Typography>
                        </Button>
                    )
                } else if (editingInstruction === row.id) {
                    return (
                        <Box ref={textFieldRef} sx={{ width: '100%' }}>
                            <TextField
                                value={row.instructions}
                                onChange={(e) => handleFieldChange(row.id, 'instructions', e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        setEditingInstruction(null)
                                    }
                                }}
                                multiline
                                minRows={1}
                                maxRows={4}
                                size="small"
                                fullWidth
                                autoFocus
                                sx={{
                                    '& .MuiInputBase-input': {
                                        fontSize: '14px',
                                        color: '#374151'
                                    }
                                }}
                            />
                        </Box>
                    )
                }
                return (
                    <Typography 
                        variant="body2" 
                        onClick={() => handleEditingInstruction(row)}
                        sx={{ 
                            fontSize: '14px', 
                            color: row.instructions ? '#374151' : '#9CA3AF',
                            cursor: 'pointer',
                            padding: '4px',
                            borderRadius: '4px',
                            minHeight: '24px',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        {row.instructions || 'Click to add instructions...'}
                    </Typography>
                )
            }
        },
        {
            header: '',
            field: 'edit',
            width: '5%',
            render: (_, row) => {
                if (row.instructions !== '' && editingInstruction !== row.id) {
                    return (
                        <IconButton onClick={() => {
                            handleEditingInstruction(row)
                        }}>
                            <EditIcon sx={{ height: '16px', width: '16px' }} />
                        </IconButton>
                    )
                } else {
                    return
                }
            }
        }
    ]

    const getTemplateTableLabel = (value) => {
        const option = templateTableOptions.find(opt => opt.uuid === value)
        return option?.extracted_table_title || option?.table_title || value
    }

    const getReplacementTableLabel = (value) => {
        const option = replacementTableOptions.find(opt => opt.uuid === value)
        return option?.extracted_table_title || option?.table_title || value
    }

    const scrollToChangedRow = (rowId) => {
        // Highlight the moved row
        setHighlightedRowId(rowId)
        
        setTimeout(() => {
            const rowElement = document.querySelector(`[data-row-id="${rowId}"]`)
            if (rowElement && scrollContainerRef.current) {
                const containerRect = scrollContainerRef.current.getBoundingClientRect()
                const rowRect = rowElement.getBoundingClientRect()
                
                const scrollTop = scrollContainerRef.current.scrollTop
                const targetScroll = scrollTop + (rowRect.top - containerRect.top) - 100 // 100px offset from top
                
                scrollContainerRef.current.scrollTo({
                    top: Math.max(0, targetScroll),
                    behavior: 'smooth'
                })
            }
        }, 100)
        
        // Remove highlight after a delay if no user interaction
        setTimeout(() => {
            setHighlightedRowId(null)
        }, 3000)
    }

    const handleRowInteraction = () => {
        if (highlightedRowId) {
            setHighlightedRowId(null)
        }
    }

    const handleFieldChange = (rowId, field, value) => {
        updateAssignment(rowId, field, value)
        
        // Scroll to the changed row's new position if section was updated
        if (field === 'section') {
            scrollToChangedRow(rowId)
        }
    }

    const handleEditingInstruction = (row) => {
        if (editingInstruction === row.id) {
            setEditingInstruction(null)
        } else {
            setEditingInstruction(row.id)
        }
    }



    const getReplacementDropdownPosition = () => {
        return calculateReplacementDropdownPosition(replacementDropdownAnchorEl)
    }

    const calculateReplacementDropdownPosition = (anchorEl) => {
        if (!anchorEl) return null

        const anchorRect = anchorEl.getBoundingClientRect()
        
        // Find the modal container to get its bounds
        const modalContainer = anchorEl.closest('[role="dialog"]') || 
                              anchorEl.closest('.MuiModal-root') ||
                              document.querySelector('[aria-labelledby="assign-tables-modal-title"]')
        
        if (!modalContainer) return null

        const modalRect = modalContainer.getBoundingClientRect()
        const dropdownHeight = 200

        // Calculate cell position within modal viewport (0 = top, 1 = bottom)
        const cellCenterY = anchorRect.top + (anchorRect.height / 2)
        const viewportPosition = (cellCenterY - modalRect.top) / modalRect.height
        
        // Calculate available space
        const spaceBelow = modalRect.bottom - anchorRect.bottom
        const spaceAbove = anchorRect.top - modalRect.top
        
        // Determine preferred vertical direction based on viewport position
        const preferredVertical = viewportPosition < 0.5 ? 'top' : 'bottom'
        const hasSpace = preferredVertical === 'top' ? spaceBelow >= dropdownHeight : spaceAbove >= dropdownHeight
        const finalVertical = hasSpace ? preferredVertical : (spaceBelow > spaceAbove ? 'top' : 'bottom')
        
        // For replacement table, always anchor to leading (left) edge of cell
        // Set coverage to match cell width for consistent appearance
        const coveragePercent = 100 // Cover the full cell width
        
        return {
            verticalOffset: finalVertical === 'top' ? 0 : 100, // Top edge or bottom edge
            horizontalOffset: 0, // Always anchor to leading (left) edge
            coveragePercent: coveragePercent,
            preferredVertical: finalVertical
        }
    }

    const handleTemplateDropdownClick = (event, rowId, useTextAnchor = false) => {
        let anchorElement = event.currentTarget
        
        // If triggered by edit button, find the text element in the same row
        if (useTextAnchor) {
            const row = event.currentTarget.closest('tr')
            const textElement = row.querySelector('[data-template-text]')
            if (textElement) {
                anchorElement = textElement
            }
        }
        
        setTemplateDropdownAnchorEl(anchorElement)
        setTemplateDropdownRowId(rowId)
        setTemplateDropdownOpen(true)
        
        // Auto-scroll to selected item within the dropdown menu only
        setTimeout(() => {
            const currentValue = tableAssignments.find(row => row.id === rowId)?.templateTable
            if (currentValue) {
                const dropdownContainer = document.getElementById(`template-dropdown-${rowId}`)
                const selectedElement = document.querySelector(`[data-template-option="${currentValue}"]`)
                
                if (dropdownContainer && selectedElement) {
                    const containerRect = dropdownContainer.getBoundingClientRect()
                    const selectedRect = selectedElement.getBoundingClientRect()
                    const relativeTop = selectedRect.top - containerRect.top + dropdownContainer.scrollTop
                    
                    // Scroll the dropdown container so the selected item is at the top
                    dropdownContainer.scrollTop = relativeTop
                }
            }
        }, 100)
    }


    const getTemplateDropdownPosition = () => {
        return calculateTemplateDropdownPosition(templateDropdownAnchorEl)
    }

    const calculateTemplateDropdownPosition = (anchorEl) => {
        if (!anchorEl) return null

        const anchorRect = anchorEl.getBoundingClientRect()
        
        // Find the modal container to get its bounds
        const modalContainer = anchorEl.closest('[role="dialog"]') || 
                              anchorEl.closest('.MuiModal-root') ||
                              document.querySelector('[aria-labelledby="assign-tables-modal-title"]')
        
        if (!modalContainer) return null

        const modalRect = modalContainer.getBoundingClientRect()
        const dropdownHeight = 200

        // Calculate cell position within modal viewport (0 = top, 1 = bottom)
        const cellCenterY = anchorRect.top + (anchorRect.height / 2)
        const viewportPosition = (cellCenterY - modalRect.top) / modalRect.height
        
        // Calculate available space
        const spaceBelow = modalRect.bottom - anchorRect.bottom
        const spaceAbove = anchorRect.top - modalRect.top
        
        // Determine preferred vertical direction based on viewport position
        const preferredVertical = viewportPosition < 0.5 ? 'top' : 'bottom'
        const hasSpace = preferredVertical === 'top' ? spaceBelow >= dropdownHeight : spaceAbove >= dropdownHeight
        const finalVertical = hasSpace ? preferredVertical : (spaceBelow > spaceAbove ? 'top' : 'bottom')
        
        // For template table, always anchor to leading (left) edge of cell
        // Set coverage to match cell width for consistent appearance
        const coveragePercent = 100 // Cover the full cell width
        
        return {
            verticalOffset: finalVertical === 'top' ? 0 : 100, // Top edge or bottom edge
            horizontalOffset: 0, // Always anchor to leading (left) edge
            coveragePercent: coveragePercent,
            preferredVertical: finalVertical
        }
    }

    const handleTemplateDropdownClose = () => {
        setTemplateDropdownOpen(false)
        setTemplateDropdownAnchorEl(null)
        setTemplateDropdownRowId(null)
    }

    const handleTemplateDropdownSelect = (value) => {
        if (templateDropdownRowId) {
            console.log('value in handleTemplateDropdownSelect', value)
            console.log('templateDropdownRowId', templateDropdownRowId)
            
            // Find the selected table
            const selectedTable = templateTableOptions.find(table => table.uuid === value)
            
            // Update template table
            updateAssignment(templateDropdownRowId, 'templateTable', value)
            
            // Update template table ID
            if (selectedTable?.id) {
                updateAssignment(templateDropdownRowId, 'templateTableId', selectedTable.id)
            }
            
            // Update section if it changed
            if (selectedTable?.section_number) {
                updateAssignment(templateDropdownRowId, 'section', selectedTable.section_number)
                scrollToChangedRow(templateDropdownRowId)
            }
        }
        handleTemplateDropdownClose()
    }

    const handleReplacementDropdownClick = (event, rowId, useTextAnchor = false) => {
        let anchorElement = event.currentTarget
        
        // If triggered by edit button, find the text element in the same row
        if (useTextAnchor) {
            const row = event.currentTarget.closest('tr')
            const textElement = row.querySelector('[data-replacement-text]')
            if (textElement) {
                anchorElement = textElement
            }
        }
        
        setReplacementDropdownAnchorEl(anchorElement)
        setReplacementDropdownRowId(rowId)
        setReplacementDropdownOpen(true)
        
        // Auto-scroll to selected item within the dropdown menu only
        setTimeout(() => {
            const currentValue = tableAssignments.find(row => row.id === rowId)?.replacementTable
            if (currentValue) {
                const dropdownContainer = document.getElementById(`replacement-dropdown-${rowId}`)
                const selectedElement = document.querySelector(`[data-replacement-option="${currentValue}"]`)
                
                if (dropdownContainer && selectedElement) {
                    const containerRect = dropdownContainer.getBoundingClientRect()
                    const selectedRect = selectedElement.getBoundingClientRect()
                    const relativeTop = selectedRect.top - containerRect.top + dropdownContainer.scrollTop
                    
                    // Scroll the dropdown container so the selected item is at the top
                    dropdownContainer.scrollTop = relativeTop
                }
            }
        }, 100)
    }

    const handleReplacementDropdownClose = () => {
        setReplacementDropdownOpen(false)
        setReplacementDropdownAnchorEl(null)
        setReplacementDropdownRowId(null)
    }

    const handleReplacementDropdownSelect = (value) => {
        if (replacementDropdownRowId) {
            console.log('value in handleReplacementDropdownSelect', value)
            console.log('replacementDropdownRowId', replacementDropdownRowId)

            const selectedReplacementTable = replacementTableOptions.find(table => table.uuid === value)

            // Update replacement table with full data
            updateReplacementTable(replacementDropdownRowId, value, selectedReplacementTable)

            // Scroll to the changed row's new position if section was updated
            if (selectedReplacementTable?.section_number) {
                scrollToChangedRow(replacementDropdownRowId)
            }
        }
        handleReplacementDropdownClose()
    }

    useEffect(() => {
        console.log('tableAssignments', tableAssignments)
    }, [tableAssignments])


    const handleAddNewAssignment = () => {
        const newAssignmentId = addAssignment()
        
        // Scroll to bottom to ensure new row and button remain visible
        setTimeout(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
                
                // Also use scrollTo for smooth behavior
                scrollContainerRef.current.scrollTo({
                    top: scrollContainerRef.current.scrollHeight,
                    behavior: 'smooth'
                })
            }
        }, 150)
        
        return newAssignmentId
    }

    const handleRemoveAll = () => {
        removeAllAssignments()
        setEditingInstruction(null)
    }

    const handleInsertAll = () => {
        // Prevent multiple clicks
        if (isProcessing) return
        
        // Get prepared assignments (filtered and formatted)
        const preparedMappings = getPreparedAssignments()
        
        console.log('Insert All - Prepared mappings:', preparedMappings)
        console.log('Insert All - Statistics:', getStatistics())
        
        // Validate we have mappings to process
        if (preparedMappings.length === 0) {
            console.warn('No valid table mappings to process')
            return
        }
        
        // Store mappings and show modal
        setPendingMappings(preparedMappings)
        setShowRegenerationModal(true)
        // Start with loading state to show the warning/confirmation
        // Keep isProcessing false so warning shows first
        setRegenerationStatus({ loading: true, success: false, error: false })
        console.log('handleInsertAll - Modal should show warning state:', {
            isProcessing: false,
            regenerationStatus: { loading: true, success: false, error: false },
            warning: true && !false // regenerationStatus.loading && !isProcessing
        })
    }
    
    const handleConfirmRegeneration = async () => {
        // Set processing state
        setIsProcessing(true)
        
        try {
            // Call the onInsertAll callback with the prepared mappings
            // This should return a promise from the API call
            if (onInsertAll && pendingMappings) {
                await onInsertAll(pendingMappings)
                
                // Only clear storage after successful API call
                // This ensures we don't lose data if the API fails
                if (sourceDocument?.document_id && tableAssignmentStorage.exists(sourceDocument.document_id)) {
                    console.log(`Clearing localStorage for document ${sourceDocument.document_id} after successful regeneration`)
                    tableAssignmentStorage.clear(sourceDocument.document_id)
                }
                
                // Show success state briefly
                setRegenerationStatus({ loading: false, success: true, error: false })
                
                // The parent component will handle navigation
                // Close modal after a brief delay to show success
                setTimeout(() => {
                    setShowRegenerationModal(false)
                    handleClose()
                    // Parent will navigate to workspace
                }, 1500)
            }
        } catch (error) {
            console.error('Failed to process Insert All:', error)
            // Show error state
            setRegenerationStatus({ loading: false, success: false, error: true })
        } finally {
            setIsProcessing(false)
        }
    }
    
    const handleCloseRegenerationModal = () => {
        // Only allow closing if not processing
        if (!isProcessing) {
            setShowRegenerationModal(false)
            setRegenerationStatus({ loading: false, success: false, error: false })
            setPendingMappings(null)
            setIsProcessing(false)
        }
    }

    const renderTableContent = () => {
        // Show loading state when fetching data
        if (loading) {
            return (
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: '300px',
                    gap: 2
                }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                        Loading table data...
                    </Typography>
                </Box>
            )
        }
        
        // Show upload prompt if user needs to upload their document
        if (needsDocumentUpload) {
            return (
                <Stack spacing={3} sx={{ alignItems: 'center', maxWidth: 500, margin: 'auto' }}>
                    <UploadFileIcon sx={{ fontSize: 64, color: '#9CA3AF' }} />
                    <Typography variant="h6" sx={{ textAlign: 'center', color: '#1F2937' }}>
                        Upload Your Document
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'center', color: '#6B7280' }}>
                        To compare tables between your template and your document, you need to upload and ingest your document first.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<UploadFileIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={loading}
                        sx={{
                            backgroundColor: '#3B82F6',
                            textTransform: 'none',
                            px: 3,
                            py: 1.5,
                            '&:hover': {
                                backgroundColor: '#2563EB'
                            }
                        }}
                    >
                        Select Document to Upload
                    </Button>
                    <Alert severity="info" sx={{ width: '100%' }}>
                        <Typography variant="body2">
                            After uploading, your document will be processed to extract tables. This may take a few moments.
                        </Typography>
                    </Alert>
                </Stack>
            )
        }
        
        if (tableAssignments.length === 0) {
            return (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    py: 4,
                    borderBottom: '1px solid #E5E7EB'
                }}>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: '#6B7280',
                            fontSize: '14px'
                        }}
                    >
                        Add a table assignment to update your document
                    </Typography>
                </Box>
            )
        }

        return (
            <Box>
                <CustomTable
                    columns={columns}
                    data={tableAssignments}
                    backgroundColor="#F9FAFB"
                    searchBar={false}
                    menuColumn={false}
                    pagination={false}
                    highlightedRowId={highlightedRowId}
                    onRowInteraction={handleRowInteraction}
                />
            </Box>
        )
    }

    return (
        <>
            <Modal
                open={open}
                onClose={handleCloseWithSave}
                aria-labelledby="assign-tables-modal-title"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                    width: needsDocumentUpload ? '600px' : '90%',
                    maxWidth: needsDocumentUpload ? '600px' : '1200px',
                    height: needsDocumentUpload ? 'auto' : '80%',
                    minHeight: needsDocumentUpload ? 'auto' : '600px',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 3,
                    backgroundColor: '#F9FAFB',
                }}
            >
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    width: '100%',
                    mb: 2,
                }}>
                    <IconButton
                        onClick={handleCloseWithSave}
                        sx={{
                            width: '30px',
                            height: '30px',
                            ml: '-7px',
                            '&:hover': {
                                backgroundColor: 'transparent',
                            }
                        }}
                    >
                        <CloseIcon sx={{ width: '16px', height: '16px', color: '#111827' }} />
                    </IconButton>
                    {/* Header - Only show when not needing document upload */}
                    {!needsDocumentUpload && !loading && (
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            width: '100%',
                        }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                }}
                            >
                                <Typography variant="h6" sx={{ fontSize: '24px', fontWeight: 700, lineHeight: '36px' }}>
                                    Assign tables
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6B7280', lineHeight: '20px', fontSize: '14px' }}>
                                    Review auto-matched replacement tables, or add more tables to replace. Add optional instructions
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {/* Debug Helper Buttons - Remove these in production */}
                            {process.env.NODE_ENV === 'development' && (
                                <>
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            console.log('=== APPLICATION LOCAL STORAGE DEBUG ===')
                                            const appKeys = []
                                            for (let i = 0; i < localStorage.length; i++) {
                                                const key = localStorage.key(i)
                                                // Filter for app-related keys
                                                if (key && (
                                                    key.includes('tableAssignments') ||
                                                    key.includes('templateTables') ||
                                                    key.includes('replacementTables') ||
                                                    key.includes('documentStatuses') ||
                                                    key.includes('activePolls') ||
                                                    key.includes('documentFilePaths') ||
                                                    key.includes('mrtTableUUIDs')
                                                )) {
                                                    appKeys.push(key)
                                                    const value = localStorage.getItem(key)
                                                    try {
                                                        const parsed = JSON.parse(value)
                                                        console.log(`📦 ${key}:`, parsed)
                                                    } catch {
                                                        console.log(`📦 ${key}:`, value)
                                                    }
                                                }
                                            }
                                            console.log(`Found ${appKeys.length} app-related localStorage entries`)
                                            console.log('Keys:', appKeys)
                                            console.log('=====================================')
                                        }}
                                        sx={{ 
                                            textTransform: 'none',
                                            p: '0px',
                                            borderRadius: '6px',
                                            boxShadow: 'inset 0px 0px 0px 1px #10B981',
                                            backgroundColor: '#ECFDF5',
                                            '&:hover': {
                                                backgroundColor: '#D1FAE5',
                                            },
                                            minWidth: 'auto'
                                        }}
                                    >
                                        <Typography sx={{ 
                                            lineHeight: '16px', 
                                            fontSize: '12px', 
                                            fontWeight: 'medium', 
                                            color: '#047857', 
                                            py: '6px', 
                                            px: '10px' 
                                        }}>
                                            🔍 Log Storage
                                        </Typography>
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            if (window.confirm('Are you sure you want to clear all app-related localStorage? This cannot be undone.')) {
                                                let clearedCount = 0
                                                const keysToRemove = []
                                                
                                                // Collect all app-related keys
                                                for (let i = 0; i < localStorage.length; i++) {
                                                    const key = localStorage.key(i)
                                                    if (key && (
                                                        key.includes('tableAssignments') ||
                                                        key.includes('templateTables') ||
                                                        key.includes('replacementTables') ||
                                                        key.includes('documentStatuses') ||
                                                        key.includes('activePolls') ||
                                                        key.includes('documentFilePaths') ||
                                                        key.includes('mrtTableUUIDs')
                                                    )) {
                                                        keysToRemove.push(key)
                                                    }
                                                }
                                                
                                                // Remove collected keys
                                                keysToRemove.forEach(key => {
                                                    localStorage.removeItem(key)
                                                    clearedCount++
                                                })
                                                
                                                console.log(`✅ Cleared ${clearedCount} app-related localStorage entries`)
                                                console.log('Removed keys:', keysToRemove)
                                                
                                                // Refresh the modal to reflect cleared state
                                                window.location.reload()
                                            }
                                        }}
                                        sx={{ 
                                            textTransform: 'none',
                                            p: '0px',
                                            borderRadius: '6px',
                                            boxShadow: 'inset 0px 0px 0px 1px #EF4444',
                                            backgroundColor: '#FEE2E2',
                                            '&:hover': {
                                                backgroundColor: '#FECACA',
                                            },
                                            minWidth: 'auto'
                                        }}
                                    >
                                        <Typography sx={{ 
                                            lineHeight: '16px', 
                                            fontSize: '12px', 
                                            fontWeight: 'medium', 
                                            color: '#B91C1C', 
                                            py: '6px', 
                                            px: '10px' 
                                        }}>
                                            🗑️ Clear Storage
                                        </Typography>
                                    </Button>
                                </>
                            )}
                            <Button
                                size="small"
                                onClick={handleRemoveAll}
                                sx={{ 
                                    textTransform: 'none',
                                    p: '0px',
                                    borderRadius: '6px',
                                    boxShadow: 'inset 0px 0px 0px 1px #D1D5DB',
                                    backgroundColor: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#F9FAFB',
                                    }
                                }}
                            >
                                <Typography sx={{ 
                                    lineHeight: '16px', 
                                    fontSize: '14px', 
                                    fontWeight: 'medium', 
                                    color: '#374151', 
                                    py: '8px', 
                                    px: '16px' 
                                }}>
                                    Remove all
                                </Typography>
                            </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
                {/* Content */}
                <Box 
                    ref={scrollContainerRef}
                    sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        flex: needsDocumentUpload ? 0 : 1, 
                        overflow: 'auto', 
                        gap: 2,
                        justifyContent: needsDocumentUpload ? 'center' : 'flex-start',
                        py: needsDocumentUpload ? 4 : 0
                    }}
                >
                    {renderTableContent()}

                    {/* Add New Table Assignment Button - Only show when not needing document upload */}
                    {!needsDocumentUpload && !loading && (
                        <SecondaryButtonFactory
                            startIcon={<AddIcon sx={{ width: '16px', height: '16px' }} />}
                            onClick={handleAddNewAssignment}
                            width='172px'
                            sx={{
                                pl: '9px',
                                pr: '11px',
                                py: '7px',
                                '& .MuiButton-startIcon': {
                                    ml: '0px',
                                    mr: '6px'
                                },
                                mb: 4
                            }}
                        >
                            <Typography sx={{ lineHeight: '16px', fontSize: '12px', fontWeight: 600, color: '#4338CA' }}>
                                New table assignment
                            </Typography>
                        </SecondaryButtonFactory>
                    )}
                </Box>

                {/* Footer - Only show when not needing document upload */}
                {!needsDocumentUpload && !loading && (
                    <Box sx={{ 
                        p: 3, 
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        <ContinueButton
                            disabled={tableAssignments.length === 0 || isProcessing}
                            text={isProcessing ? "Processing..." : "Insert all"}
                            onClick={handleInsertAll}
                            bold={true}
                            loading={isProcessing}
                        />
                    </Box>
                )}
            </Box>
            </Modal>
            
            {/* Regeneration Modal - Using warning state for initial notice */}
            <GeneratingTemplateModal
                open={showRegenerationModal}
                onClose={handleCloseRegenerationModal}
                loading={isProcessing}
                success={regenerationStatus.success}
                error={regenerationStatus.error}
                warning={regenerationStatus.loading && !isProcessing}
                warningMessage="Document Regeneration Notice"
                warningSubMessage="This will lock the document during regeneration. You'll be redirected to the workspace and can work on other documents while you wait. You'll receive an email when it's ready."
                onWarning={handleConfirmRegeneration}
                onCancel={handleCloseRegenerationModal}
                buttonText="Continue with Regeneration"
                cancelButtonText="Cancel"
                loadingMessage="Processing Regeneration"
                loadingSubMessage="Starting table replacement process..."
                successMessage="Regeneration Started"
                successSubMessage="The document is being regenerated in the background."
                successNote="You'll receive an email when it's ready."
                successButtonText="Back to Workspace"
                errorMessage="Regeneration failed. Please try again."
                errorButtonText="Try Again"
                onSuccess={() => {
                    // Success is handled in handleConfirmRegeneration
                }}
                onError={() => {
                    // Allow retry
                    setRegenerationStatus({ loading: true, success: false, error: false })
                    handleConfirmRegeneration()
                }}
            />
            
            {/* Hidden file input */}
            <input
                type="file"
                multiple
                accept=".pdf,.docx,.xlsx,.csv,.rtf"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />
        </>
    )
}

export default AssignTablesModal
